import {
  AcademicCapIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ReceiptPercentIcon,
  TagIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { usePageMeta } from '../lib/pageMeta';
import { downloadCsv, formatDateForCsv } from '../lib/csvExport';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const tok = () => localStorage.getItem('admin_token');

const apiFetch = (url) =>
  fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${tok()}` } }).then((r) => r.json());

/* ── helpers ── */
const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_COLORS = {
  active:       'bg-green-100 text-green-700 border-green-200',
  transferred:  'bg-blue-100  text-blue-700  border-blue-200',
  discontinued: 'bg-red-100   text-red-700   border-red-200',
  dropped:      'bg-red-100   text-red-700   border-red-200',
};

const FEE_STATUS_COLORS = {
  paid:         'bg-emerald-100 text-emerald-700 border-emerald-200',
  partial:      'bg-amber-100   text-amber-700   border-amber-200',
  pending:      'bg-gray-100    text-gray-600    border-gray-200',
  discontinued: 'bg-red-100     text-red-700     border-red-200',
  no_invoice:   'bg-purple-100  text-purple-700  border-purple-200',
};

const PIE_COLORS = ['#10b981', '#f59e0b', '#6b7280', '#ef4444'];

/* ══════════════════════════════════════════════════════════════════════════ */
export default function BatchAnalytics() {
  usePageMeta({
    title: 'Batch Analytics | Emax Technologies',
    description: 'Admin analytics: student enrollments, fee collections and trainer payments per batch.',
  });

  const [batches, setBatches]   = useState([]);
  const [batchId, setBatchId]   = useState('all');
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [batchLoading, setBatchLoading] = useState(true);
  const [studentsPage, setStudentsPage] = useState(1);
  const BATCH_PAGE_SIZE = 10;

  // Date-range filter (draft = in the inputs, applied = sent to API)
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd,   setDraftEnd]   = useState('');
  const [appliedStart, setAppliedStart] = useState('');
  const [appliedEnd,   setAppliedEnd]   = useState('');

  /* load batch list once */
  useEffect(() => {
    apiFetch('/analytics/batches')
      .then((r) => setBatches(r.data || []))
      .catch(() => setBatches([]))
      .finally(() => setBatchLoading(false));
  }, []);

  /* load analytics whenever batch or applied date range changes */
  useEffect(() => {
    setLoading(true);
    setData(null);
    setStudentsPage(1);
    const params = new URLSearchParams();
    if (appliedStart) params.set('startDate', appliedStart);
    if (appliedEnd)   params.set('endDate',   appliedEnd);
    const qs = params.toString() ? `?${params.toString()}` : '';
    apiFetch(`/analytics/batch/${batchId}${qs}`)
      .then((r) => setData(r.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [batchId, appliedStart, appliedEnd]);

  const handleApplyDateRange = () => {
    setAppliedStart(draftStart);
    setAppliedEnd(draftEnd);
  };

  const handleClearDateRange = () => {
    setDraftStart(''); setDraftEnd('');
    setAppliedStart(''); setAppliedEnd('');
  };

  /* ── overview stat cards ── */
  const StatCard = ({ icon: Icon, label, value, sub, color = 'indigo' }) => {
    const colors = {
      indigo:  'bg-indigo-50  text-indigo-700  border-indigo-200',
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      amber:   'bg-amber-50   text-amber-700   border-amber-200',
      red:     'bg-red-50     text-red-700     border-red-200',
      blue:    'bg-blue-50    text-blue-700    border-blue-200',
    };
    return (
      <Card className={`border ${colors[color]}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</p>
              <p className="text-2xl font-bold mt-1">{value}</p>
              {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
            </div>
            <Icon className="h-8 w-8 opacity-30" />
          </div>
        </CardContent>
      </Card>
    );
  };

  const selectedBatch = batches.find((b) => b._id === batchId);
  const batchLabel    = selectedBatch ? `${selectedBatch.name}-${selectedBatch.code || ''}` : 'all';

  const handleExportStudents = () => {
    if (!data?.studentRows?.length) return;
    const headers = ['Student Name', 'Email', 'Phone', 'Batch', 'Joined Date', 'Enrollment Status', 'Fee Status', 'Total Fee (₹)', 'Paid (₹)', 'Outstanding (₹)', 'Installments'];
    const rows = data.studentRows.map((s) => [
      s.name        || '-',
      s.email       || '-',
      s.phone       || '-',
      s.batchName   || '-',
      s.joinDate    ? formatDateForCsv(s.joinDate) : '-',
      s.status      || '-',
      s.feeStatus   || '-',
      s.totalFee    || 0,
      s.paidAmount  || 0,
      s.outstanding || 0,
      s.installments || 0,
    ]);
    downloadCsv(headers, rows, `batch-students-${batchLabel}-${new Date().toISOString().slice(0,10)}.csv`);
    toast.success(`Exported ${rows.length} student records`);
  };

  const handleExportTrainerPayments = () => {
    if (!data?.trainerPayments?.length) return;
    const headers = ['Trainer', 'Email', 'Date', 'Amount (₹)', 'Payment Mode', 'Batches', 'Description', 'Recorded By'];
    const rows = data.trainerPayments.map((p) => [
      p.trainerId?.name  || '-',
      p.trainerId?.email || '-',
      p.date ? formatDateForCsv(p.date) : '-',
      p.amount || 0,
      (p.paymentMethod || '').replace(/_/g, ' ') || '-',
      [...(p.batchIds || []), p.batchId].filter(Boolean).map(b => b.name || b).join('; ') || '-',
      p.description || '-',
      p.createdBy?.name || '-',
    ]);
    downloadCsv(headers, rows, `trainer-payments-${batchLabel}-${new Date().toISOString().slice(0,10)}.csv`);
    toast.success(`Exported ${rows.length} payment records`);
  };

  return (
    <div className="space-y-5">
      {/* ── Page header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ChartBarIcon className="h-7 w-7 text-indigo-600" />
            Batch Analytics
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Enrollments, fee collections, expenses &amp; net revenue — admin view only
          </p>
        </div>
      </div>

      {/* ── Filter card ── */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-end gap-4">

          {/* Batch selector */}
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <FunnelIcon className="h-3.5 w-3.5" /> Select Batch
            </label>
            <select
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              disabled={batchLoading}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white disabled:opacity-50"
            >
              <option value="all">📊 All Batches (Combined)</option>
              {batches.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name} — {b.courseId?.name || ''} ({b.status})
                </option>
              ))}
            </select>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px bg-gray-200 self-stretch mx-1" />

          {/* Date range */}
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                From Date
              </label>
              <input
                type="date"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white w-full sm:w-auto"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                To Date
              </label>
              <input
                type="date"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white w-full sm:w-auto"
              />
            </div>

            {/* Action buttons */}
            <div className="flex items-end gap-2">
              <button
                onClick={handleApplyDateRange}
                className="px-4 py-2.5 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-colors whitespace-nowrap"
              >
                Apply Period
              </button>
              {(appliedStart || appliedEnd) && (
                <button
                  onClick={handleClearDateRange}
                  className="px-3 py-2.5 text-sm font-medium border border-gray-200 hover:bg-gray-50 text-gray-500 rounded-xl transition-colors whitespace-nowrap"
                  title="Clear date filter"
                >
                  ✕ Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Active period pill */}
        {(appliedStart || appliedEnd) && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-indigo-700">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-100 border border-indigo-200 font-semibold">
              📅 Period active
            </span>
            {appliedStart && (
              <span>From <strong>{new Date(appliedStart).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</strong></span>
            )}
            {appliedEnd && (
              <span>to <strong>{new Date(appliedEnd).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</strong></span>
            )}
            <span className="text-indigo-400">— filters enrollments, payments &amp; expenses</span>
          </div>
        )}
      </div>

      {/* Selected batch info strip */}
      {selectedBatch && (
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl px-5 py-3.5 flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
          <span className="text-indigo-800"><strong>Batch:</strong> {selectedBatch.name} <span className="text-indigo-500">({selectedBatch.code})</span></span>
          <span className="text-indigo-800"><strong>Course:</strong> {selectedBatch.courseId?.name}</span>
          <span className="text-indigo-800"><strong>Mode:</strong> <span className="capitalize">{selectedBatch.mode}</span></span>
          <span>
            <strong className="text-indigo-800">Status:</strong>{' '}
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
              selectedBatch.status === 'active'    ? 'bg-green-100 text-green-700'  :
              selectedBatch.status === 'completed' ? 'bg-gray-100 text-gray-600'   :
              selectedBatch.status === 'upcoming'  ? 'bg-blue-100 text-blue-700'   :
              'bg-yellow-100 text-yellow-700'
            }`}>{selectedBatch.status}</span>
          </span>
          <span className="text-indigo-800"><strong>Start:</strong> {fmtDate(selectedBatch.startDate)}</span>
          <span className="text-indigo-800"><strong>End:</strong> {fmtDate(selectedBatch.endDate)}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        </div>
      )}

      {!loading && data && (
        <>
          {/* ── Row 1: Fee Collection cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={UserGroupIcon}           label="Total Students"  value={data.totalStudents}         sub={`${data.activeStudents} active`}             color="indigo"  />
            <StatCard icon={CurrencyRupeeIcon}       label="Total Fees"      value={fmt(data.totalFees)}        sub="invoiced"                                    color="blue"    />
            <StatCard icon={CheckCircleIcon}         label="Collected"       value={fmt(data.totalCollected)}   sub="from students"                               color="emerald" />
            <StatCard icon={ExclamationTriangleIcon} label="Outstanding"     value={fmt(data.totalOutstanding)} sub="yet to collect"                              color="amber"   />
          </div>

          {/* ── Row 2: Expense / Net cards ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={BanknotesIcon}      label="Trainer Paid"   value={fmt(data.totalTrainerPaid)} sub={`${data.trainerPayments?.length || 0} payments`} color="red"  />
            <StatCard icon={ReceiptPercentIcon} label="Total Expenses" value={fmt(data.totalExpenses || 0)} sub={`${data.expenses?.length || 0} entries`}        color="red"  />
            <div className={`rounded-2xl border p-5 flex items-start gap-4 ${
              (data.netRevenue || 0) >= 0
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <ArrowTrendingUpIcon className={`h-8 w-8 ${(data.netRevenue || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider opacity-70 ${(data.netRevenue || 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                  Net Revenue
                </p>
                <p className={`text-2xl font-bold mt-0.5 ${(data.netRevenue || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {fmt(data.netRevenue || 0)}
                </p>
                <p className={`text-xs mt-0.5 opacity-60 ${(data.netRevenue || 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                  Collected − Expenses
                </p>
              </div>
            </div>
          </div>

          {/* ── Combined monthly chart + Fee status pie ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 border border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <ChartBarIcon className="h-4 w-4 text-indigo-500" />
                  Monthly Collections vs Expenses
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(!data.combinedMonthlyTrend || data.combinedMonthlyTrend.length === 0) ? (
                  <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No data yet</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.combinedMonthlyTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="collected" fill="#6366f1" radius={[4, 4, 0, 0]} name="Collected" />
                      <Bar dataKey="expenses"  fill="#f43f5e" radius={[4, 4, 0, 0]} name="Expenses" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* ── Fee status pie ── */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <AcademicCapIcon className="h-4 w-4 text-emerald-500" />
                  Fee Status Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.values(data.feeStatusSummary).every((v) => v === 0) ? (
                  <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No invoices found</div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Paid',         value: data.feeStatusSummary.paid },
                            { name: 'Partial',      value: data.feeStatusSummary.partial },
                            { name: 'Pending',      value: data.feeStatusSummary.pending },
                            { name: 'Discontinued', value: data.feeStatusSummary.discontinued },
                          ].filter((d) => d.value > 0)}
                          cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                          paddingAngle={3} dataKey="value"
                        >
                          {PIE_COLORS.map((color, i) => <Cell key={i} fill={color} />)}
                        </Pie>
                        <Tooltip />
                        <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {[
                        { label: 'Paid',         val: data.feeStatusSummary.paid,         color: 'text-emerald-600' },
                        { label: 'Partial',      val: data.feeStatusSummary.partial,      color: 'text-amber-600'   },
                        { label: 'Pending',      val: data.feeStatusSummary.pending,      color: 'text-gray-500'    },
                        { label: 'Discontinued', val: data.feeStatusSummary.discontinued, color: 'text-red-600'     },
                      ].map((item) => (
                        <div key={item.label} className="text-center">
                          <p className={`text-xl font-bold ${item.color}`}>{item.val}</p>
                          <p className="text-xs text-gray-500">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Expenses for this batch / period ── */}
          {(data.expenses?.length > 0 || data.expenseByCategory?.length > 0) && (
            <Card className="border border-rose-200 bg-rose-50/30">
              <CardHeader className="border-b border-rose-100 pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-sm font-semibold text-rose-800 flex items-center gap-2">
                    <ReceiptPercentIcon className="h-4 w-4 text-rose-500" />
                    Expenses — {batchId === 'all' ? 'All Batches' : selectedBatch?.name}
                    <span className="ml-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold border border-rose-200">
                      Total: {fmt(data.totalExpenses)}
                    </span>
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* Category breakdown mini-bar */}
                {data.expenseByCategory?.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {data.expenseByCategory.map(c => {
                      const pct = data.totalExpenses ? Math.round((c.total / data.totalExpenses) * 100) : 0;
                      return (
                        <div key={c.category} className="bg-white border border-rose-100 rounded-xl p-3">
                          <p className="text-xs text-gray-500 truncate">{c.category}</p>
                          <p className="text-sm font-bold text-gray-800 mt-0.5">{fmt(c.total)}</p>
                          <div className="mt-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-rose-400 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-[10px] text-gray-400 mt-0.5">{pct}%</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Expense rows */}
                {data.expenses?.length > 0 && (
                  <div className="bg-white rounded-xl border border-rose-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs text-white uppercase tracking-wide">Date</TableHead>
                            <TableHead className="text-xs text-white uppercase tracking-wide">Title</TableHead>
                            <TableHead className="text-xs text-white uppercase tracking-wide">Category</TableHead>
                            <TableHead className="text-xs text-white uppercase tracking-wide">Staff / Trainer</TableHead>
                            <TableHead className="text-xs text-white uppercase tracking-wide">Payment</TableHead>
                            <TableHead className="text-xs text-white uppercase tracking-wide text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.expenses.map((e) => (
                            <TableRow key={e._id} className="hover:bg-rose-50/50">
                              <TableCell className="text-xs text-gray-500 whitespace-nowrap">{fmtDate(e.date)}</TableCell>
                              <TableCell>
                                <p className="text-sm font-medium text-gray-800">{e.title}</p>
                                {e.description && <p className="text-xs text-gray-400 truncate max-w-[180px]">{e.description}</p>}
                              </TableCell>
                              <TableCell>
                                <span className="inline-block px-2 py-0.5 text-[10px] rounded-full bg-rose-50 text-rose-700 border border-rose-200 capitalize">
                                  {e.category === 'custom' ? e.customCategory : e.category.replace(/_/g, ' ')}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs text-gray-600">
                                {e.staffId?.name   && <span className="flex items-center gap-1">👤 {e.staffId.name}</span>}
                                {e.trainerId?.name && <span className="flex items-center gap-1">🎓 {e.trainerId.name}</span>}
                                {!e.staffId && !e.trainerId && <span className="text-gray-300">—</span>}
                              </TableCell>
                              <TableCell>
                                <span className="text-[10px] capitalize text-gray-500">
                                  {(e.paymentMethod || '').replace(/_/g, ' ')}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm font-bold text-rose-700 text-right whitespace-nowrap">
                                {fmt(e.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Students Table ── */}
          <Card className="border border-gray-200">
            <CardHeader className="border-b border-gray-100 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <UserGroupIcon className="h-4 w-4 text-indigo-500" />
                  Enrolled Students ({data.totalStudents})
                </CardTitle>
                {data.studentRows.length > 0 && (
                  <button
                    onClick={handleExportStudents}
                    className="flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 transition-colors"
                  >
                    ↓ Export CSV
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {data.studentRows.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <UserGroupIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                  <p className="text-sm">No students enrolled in this batch.</p>
                </div>
              ) : (
                <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs text-white uppercase tracking-wide">Student</TableHead>
                        {batchId === 'all' && <TableHead className="text-xs text-white uppercase tracking-wide">Batch</TableHead>}
                        <TableHead className="text-xs text-white uppercase tracking-wide">Joined</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide">Status</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide">Fee Status</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide text-right">Total Fee</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide text-right">Paid</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide text-right">Outstanding</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.studentRows
                        .slice((studentsPage - 1) * BATCH_PAGE_SIZE, studentsPage * BATCH_PAGE_SIZE)
                        .map((s, i) => (
                        <TableRow key={i} className="hover:bg-gray-50">
                          <TableCell>
                            <p className="text-sm font-medium text-gray-900">{s.name}</p>
                            <p className="text-xs text-gray-500">{s.email}</p>
                          </TableCell>
                          {batchId === 'all' && (
                            <TableCell className="text-sm text-gray-600">{s.batchName}</TableCell>
                          )}
                          <TableCell className="text-sm text-gray-600 whitespace-nowrap">{fmtDate(s.joinDate)}</TableCell>
                          <TableCell>
                            <span className={`inline-block px-2 py-0.5 text-xs rounded-full border capitalize ${STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-600'}`}>
                              {s.status}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-block px-2 py-0.5 text-xs rounded-full border capitalize ${FEE_STATUS_COLORS[s.feeStatus] || 'bg-gray-100 text-gray-600'}`}>
                              {s.feeStatus.replace('_', ' ')}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm font-medium text-gray-800 text-right">{s.totalFee > 0 ? fmt(s.totalFee) : '—'}</TableCell>
                          <TableCell className="text-sm font-semibold text-emerald-700 text-right">{s.paidAmount > 0 ? fmt(s.paidAmount) : '—'}</TableCell>
                          <TableCell className={`text-sm font-semibold text-right ${s.outstanding > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {s.outstanding > 0 ? fmt(s.outstanding) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Students pagination */}
                {data.studentRows.length > BATCH_PAGE_SIZE && (() => {
  const totalPages = Math.ceil(data.studentRows.length / BATCH_PAGE_SIZE);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <p className="text-xs text-gray-500">
        Showing {(studentsPage - 1) * BATCH_PAGE_SIZE + 1}–{Math.min(studentsPage * BATCH_PAGE_SIZE, data.studentRows.length)} of {data.studentRows.length} students
      </p>
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="outline" disabled={studentsPage <= 1}
          onClick={() => setStudentsPage(1)} className="text-xs h-7">« First</Button>
        <Button size="sm" variant="outline" disabled={studentsPage <= 1}
          onClick={() => setStudentsPage(p => p - 1)} className="text-xs h-7">← Prev</Button>
        {studentsPage > 3 && totalPages > 5 && (
          <span className="text-xs text-gray-400 px-1">…</span>
        )}
        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
          const p = totalPages <= 5 ? i + 1 : studentsPage <= 3 ? i + 1 : studentsPage >= totalPages - 2 ? totalPages - 4 + i : studentsPage - 2 + i;
          return (
            <Button key={p} size="sm" variant={p === studentsPage ? 'default' : 'outline'}
              onClick={() => setStudentsPage(p)}
              className={`text-xs h-7 w-7 ${p === studentsPage ? 'bg-blue-600 text-white' : ''}`}>{p}</Button>
          );
        })}
        {studentsPage < totalPages - 2 && totalPages > 5 && (
          <span className="text-xs text-gray-400 px-1">…</span>
        )}
        <Button size="sm" variant="outline" disabled={studentsPage >= totalPages}
          onClick={() => setStudentsPage(p => p + 1)} className="text-xs h-7">Next →</Button>
        <Button size="sm" variant="outline" disabled={studentsPage >= totalPages}
          onClick={() => setStudentsPage(totalPages)} className="text-xs h-7">Last »</Button>
      </div>
    </div>
  );
})()}
</>
)} 
            </CardContent>
          </Card>

          {/* ── Trainer Payments ── */}
          <Card className="border border-gray-200">
            <CardHeader className="border-b border-gray-100 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <BanknotesIcon className="h-4 w-4 text-emerald-500" />
                  Trainer Salary Payments
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 font-semibold ml-2">
                    Total: {fmt(data.totalTrainerPaid)}
                  </Badge>
                </CardTitle>
                {data.trainerPayments.length > 0 && (
                  <button
                    onClick={handleExportTrainerPayments}
                    className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
                  >
                    ↓ Export CSV
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {data.trainerPayments.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <BanknotesIcon className="h-10 w-10 mx-auto mb-2 text-gray-200" />
                  <p className="text-sm">No trainer payments recorded for this batch.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs text-white uppercase tracking-wide">Trainer</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide">Date</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide text-right">Amount</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide">Payment Mode</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide">Batches</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide">Description</TableHead>
                        <TableHead className="text-xs text-white uppercase tracking-wide">Recorded By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.trainerPayments.map((p) => {
                        const allBatches = [...(p.batchIds || []), p.batchId].filter(Boolean);
                        return (
                          <TableRow key={p._id} className="hover:bg-gray-50">
                            <TableCell>
                              <p className="text-sm font-medium text-gray-900">{p.trainerId?.name || '—'}</p>
                              <p className="text-xs text-gray-500">{p.trainerId?.email || ''}</p>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 whitespace-nowrap">{fmtDate(p.date)}</TableCell>
                            <TableCell className="text-sm font-semibold text-emerald-700 text-right">{fmt(p.amount)}</TableCell>
                            <TableCell>
                              <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200 capitalize">
                                {(p.paymentMethod || '').replace(/_/g, ' ')}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 max-w-[160px]">
                              {allBatches.length > 0
                                ? allBatches.map((b) => b.name || b).join(', ')
                                : <span className="text-gray-400 italic">Not specified</span>}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500 max-w-[140px] truncate">{p.description || '—'}</TableCell>
                            <TableCell className="text-sm text-gray-600">{p.createdBy?.name || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Trainer assignments strip ── */}
          {data.trainerAssignments.length > 0 && (
            <Card className="border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-3">
                <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <ClockIcon className="h-4 w-4 text-purple-500" />
                  Assigned Trainers
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  {data.trainerAssignments.map((ta) => (
                    <div key={ta._id} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                      <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-semibold text-sm">
                        {(ta.trainerId?.name || 'T').charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{ta.trainerId?.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{ta.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!loading && !data && (
        <div className="text-center py-20 text-gray-400">
          <ChartBarIcon className="h-14 w-14 mx-auto mb-3 text-gray-200" />
          <p className="text-sm">Failed to load analytics. Please try again.</p>
        </div>
      )}
    </div>
  );
}
